import React from 'react';
import { Box, Slider, Typography, IconButton } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface ZoomControlsProps {
    dataLength: number;
    startIndex: number;
    endIndex: number;
    onRangeChange: (startIndex: number, endIndex: number) => void;
    onReset: () => void;
}

export const ZoomControls: React.FC<ZoomControlsProps> = ({
    dataLength,
    startIndex,
    endIndex,
    onRangeChange,
    onReset
}) => {
    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            onRangeChange(newValue[0], newValue[1]);
        }
    };

    const isZoomed = startIndex > 0 || endIndex < dataLength - 1;

    return (
        <Box sx={{
            px: { xs: 1, sm: 2 },
            py: 1,
            borderTop: '1px solid #e0e0e0',
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 2 },
                mb: 1
            }}>
                <Typography variant="caption" sx={{ minWidth: '80px', display: { xs: 'none', sm: 'block' } }}>
                    Zoom Range:
                </Typography>
                <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 0 } }}>
                    <Slider
                        value={[startIndex, endIndex]}
                        onChange={handleSliderChange}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `Point ${value + 1}`}
                        min={0}
                        max={dataLength - 1}
                        step={1}
                        marks={[
                            { value: 0, label: 'Start' },
                            { value: dataLength - 1, label: 'End' }
                        ]}
                        sx={{
                            '& .MuiSlider-thumb': {
                                width: { xs: 24, sm: 20 },
                                height: { xs: 24, sm: 20 },
                                '&:hover': {
                                    boxShadow: '0px 0px 0px 8px rgba(25, 118, 210, 0.16)',
                                },
                            },
                            '& .MuiSlider-rail': {
                                height: 6,
                                backgroundColor: '#e0e0e0',
                            },
                            '& .MuiSlider-track': {
                                height: 6,
                                backgroundColor: '#1976d2',
                            },
                            '& .MuiSlider-mark': {
                                height: 8,
                                width: 2,
                                backgroundColor: '#666',
                            },
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: -1, sm: 0 } }}>
                    <IconButton
                        onClick={onReset}
                        disabled={!isZoomed}
                        size="small"
                        title="Reset zoom to full range"
                        sx={{
                            opacity: isZoomed ? 1 : 0.5,
                            '&:hover': {
                                backgroundColor: isZoomed ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                            }
                        }}
                    >
                        <RestartAltIcon />
                    </IconButton>
                </Box>
            </Box>       
        </Box>
    );
};